<template>
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black/80 backdrop-blur-sm" @click="closeModal"></div>
      
      <!-- Modal -->
      <div class="relative bg-casino-card border border-white/10 rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="p-6 border-b border-white/5">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <img src="/todofino_icon.png" alt="TodoFino" class="w-10 h-10 rounded-xl object-cover" />
              <div>
                <h2 class="text-xl font-bold text-white">{{ otpStep ? t('auth.verifyEmailTitle') : t('auth.createAccount') }}</h2>
                <p class="text-sm text-gray-400">{{ otpStep ? t('auth.enterOtpToContinue') : t('auth.registerSubtitle') }}</p>
              </div>
            </div>
            <button @click="closeModal" class="btn-icon p-2">
              <X class="w-5 h-5" />
            </button>
          </div>
        </div>

        <!-- Form (Scrollable) -->
        <div class="flex-1 overflow-y-auto p-6">
          <form @submit.prevent="otpStep ? handleVerifyOtp() : handleRegister()" class="space-y-5">
            <!-- Error -->
            <div v-if="authStore.error" class="bg-casino-loss/10 border border-casino-loss/30 rounded-lg p-3">
              <p class="text-casino-loss text-sm">{{ authStore.error }}</p>
            </div>

            <!-- Name Fields -->
            <div v-if="!otpStep" class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">{{ t('auth.firstName') }}</label>
                <div class="relative">
                  <User class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    v-model="form.first_name"
                    type="text"
                    required
                    class="input pl-10"
                    :placeholder="t('auth.firstNamePlaceholder')"
                    :disabled="loading"
                  />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">{{ t('auth.lastName') }}</label>
                <input
                  v-model="form.last_name"
                  type="text"
                  required
                  class="input"
                  :placeholder="t('auth.lastNamePlaceholder')"
                  :disabled="loading"
                />
              </div>
            </div>

            <!-- Username -->
            <div v-if="!otpStep">
              <label class="block text-sm font-medium text-gray-300 mb-2">{{ t('auth.username') }}</label>
              <div class="relative">
                <AtSign class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  v-model="form.username"
                  type="text"
                  required
                  class="input pl-10"
                  :placeholder="t('auth.registerUsernamePlaceholder')"
                  :disabled="loading"
                />
              </div>
            </div>

            <!-- Email -->
            <div v-if="!otpStep">
              <label class="block text-sm font-medium text-gray-300 mb-2">{{ t('auth.email') }}</label>
              <div class="relative">
                <Mail class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  v-model="form.email"
                  type="email"
                  required
                  class="input pl-10"
                  :placeholder="t('auth.emailPlaceholder')"
                  :disabled="loading"
                />
              </div>
            </div>

            <!-- Password -->
            <div v-if="!otpStep">
              <label class="block text-sm font-medium text-gray-300 mb-2">{{ t('auth.password') }}</label>
              <div class="relative">
                <Lock class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  required
                  minlength="8"
                  class="input pl-10 pr-10"
                  :placeholder="t('auth.passwordMinPlaceholder')"
                  :disabled="loading"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                >
                  <Eye v-if="!showPassword" class="w-5 h-5" />
                  <EyeOff v-else class="w-5 h-5" />
                </button>
              </div>
            </div>

            <!-- OTP -->
            <div v-if="otpStep">
              <label class="block text-sm font-medium text-gray-300 mb-2">{{ t('auth.otpCode') }}</label>
              <OtpCodeInput v-model="otpCode" :disabled="loading" />
              <p v-if="otpRemainingSeconds > 0" class="mt-2 text-xs text-gray-500">
                {{ t('auth.resendIn', { time: formatSeconds(otpRemainingSeconds) }) }}
              </p>
              <p class="mt-2 text-xs text-gray-500">{{ t('auth.checkEmailForCode') }}</p>
              <button
                type="button"
                class="mt-3 text-sm text-casino-primary hover:text-casino-primary-light disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-casino-primary"
                :disabled="loading || otpRemainingSeconds > 0"
                @click="handleResendOtp"
              >
                {{ t('auth.resendCode') }}
              </button>
            </div>

            <!-- Terms -->
            <label v-if="!otpStep" class="flex items-start gap-2 cursor-pointer">
              <input
                v-model="form.terms"
                type="checkbox"
                required
                class="w-4 h-4 mt-0.5 rounded border-white/20 bg-white/5 text-casino-primary focus:ring-casino-primary"
              />
              <span class="text-sm text-gray-400">
                {{ t('auth.acceptTermsPrefix') }}
                <a href="#" class="text-casino-primary hover:underline">{{ t('auth.termsOfService') }}</a>
                {{ t('auth.and') }}
                <a href="#" class="text-casino-primary hover:underline">{{ t('auth.privacyPolicy') }}</a>
              </span>
            </label>

            <!-- Submit -->
            <button
              type="submit"
              :disabled="loading || (!otpStep && !form.terms) || (otpStep && String(otpCode || '').replace(/\\D/g, '').length !== 6)"
              class="w-full py-3 bg-casino-primary hover:bg-casino-primary-light text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Loader2 v-if="loading" class="w-5 h-5 animate-spin" />
              <UserPlus v-else class="w-5 h-5" />
              {{ loading ? (otpStep ? t('auth.verifying') : t('auth.creatingAccount')) : (otpStep ? t('auth.verifyCode') : t('auth.createAccount')) }}
            </button>
          </form>

          <!-- Divider -->
          <div v-if="!otpStep" class="relative my-6">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-white/10"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-casino-card text-gray-500">{{ t('auth.orRegisterWith') }}</span>
            </div>
          </div>

          <!-- Social Login -->
          <div v-if="!otpStep" class="grid grid-cols-2 gap-3">
            <button class="flex items-center justify-center gap-2 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors text-sm text-gray-300">
              <span class="text-lg">G</span>
              Google
            </button>
            <button class="flex items-center justify-center gap-2 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors text-sm text-gray-300">
              <span class="text-lg">𝕏</span>
              Twitter
            </button>
          </div>

          <!-- Login Link -->
          <p v-if="!otpStep" class="mt-6 text-center text-sm text-gray-400">
            {{ t('auth.haveAccount') }}
            <button
              type="button"
              @click="switchToLogin"
              class="text-casino-primary hover:text-casino-primary-light font-medium ml-1"
            >
              {{ t('auth.login') }}
            </button>
          </p>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import OtpCodeInput from '@/components/OtpCodeInput.vue'
import { 
  X, 
  Eye, 
  EyeOff, 
  Loader2,
  UserPlus,
  Mail,
  Lock,
  User,
  AtSign
} from 'lucide-vue-next'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'switchToLogin', 'success'])

const { t, locale } = useI18n()
const authStore = useAuthStore()

const form = ref({
  first_name: '',
  last_name: '',
  username: '',
  email: '',
  password: '',
  terms: false
})
const showPassword = ref(false)
const loading = ref(false)
const otpStep = ref(false)
const otpToken = ref('')
const otpCode = ref('')
const otpExpiresAt = ref(0)
const otpRemainingSeconds = ref(0)
let otpTimer = null

const formatSeconds = (value) => {
  const total = Math.max(0, Number(value) || 0)
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

const stopOtpTimer = () => {
  if (otpTimer) {
    clearInterval(otpTimer)
    otpTimer = null
  }
}

const updateOtpRemaining = () => {
  if (!otpExpiresAt.value) {
    otpRemainingSeconds.value = 0
    return
  }
  otpRemainingSeconds.value = Math.max(0, Math.ceil((otpExpiresAt.value - Date.now()) / 1000))
  if (otpRemainingSeconds.value <= 0) {
    stopOtpTimer()
  }
}

const startOtpTimer = ({ expiresInSeconds }) => {
  const ttlSeconds = Number(expiresInSeconds)
  otpExpiresAt.value = Number.isFinite(ttlSeconds) && ttlSeconds > 0
    ? Date.now() + Math.floor(ttlSeconds * 1000)
    : 0
  stopOtpTimer()
  updateOtpRemaining()
  if (otpExpiresAt.value) {
    otpTimer = setInterval(updateOtpRemaining, 1000)
  }
}

onBeforeUnmount(() => stopOtpTimer())

const handleRegister = async () => {
  if (!form.value.terms) return
  
  loading.value = true
  authStore.clearError()
  
  try {
    const name = `${form.value.first_name} ${form.value.last_name}`.trim()
    const result = await authStore.register({
      name: name || form.value.username,
      username: form.value.username,
      email: form.value.email,
      password: form.value.password,
      country: 'AR',
      language: locale.value,
    })
    
    if (result.success) {
      if (result.otpRequired) {
        otpStep.value = true
        otpToken.value = String(result.otpToken || '').trim()
        otpCode.value = ''
        startOtpTimer({ expiresInSeconds: result.expiresIn })
        return
      }

      emit('success')
      closeModal()
    }
  } catch (error) {
    console.error('Register error:', error)
  } finally {
    loading.value = false
  }
}

const handleVerifyOtp = async () => {
  loading.value = true
  authStore.clearError()

  try {
    const result = await authStore.verifyOtp({
      otpToken: otpToken.value,
      code: otpCode.value,
    })

    if (result.success) {
      emit('success')
      closeModal()
    }
  } catch (error) {
    console.error('OTP error:', error)
  } finally {
    loading.value = false
  }
}

const handleResendOtp = async () => {
  loading.value = true
  authStore.clearError()

  try {
    const result = await authStore.login({
      username: form.value.username,
      password: form.value.password,
    })

    if (result.success && result.otpRequired) {
      otpToken.value = String(result.otpToken || '').trim()
      otpCode.value = ''
      startOtpTimer({ expiresInSeconds: result.expiresIn })
    }
  } catch (error) {
    console.error('Resend OTP error:', error)
  } finally {
    loading.value = false
  }
}

const switchToLogin = () => {
  emit('switchToLogin')
}

const closeModal = () => {
  authStore.clearError()
  otpStep.value = false
  otpToken.value = ''
  otpCode.value = ''
  otpExpiresAt.value = 0
  otpRemainingSeconds.value = 0
  stopOtpTimer()
  form.value = {
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    password: '',
    terms: false
  }
  emit('close')
}
</script>
